import { useEffect, useMemo, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

const UNLINK = "";
/** Default half-range in meters. Old ±20 m clamped real misalignment. */
const SLIDER_BASE_M = 200;
const SLIDER_STEP_M = 0.1;
const SLIDER_EXPAND_PAD_M = 50;
/** Height nudge — mm-looking values are converted; never apply 2800 as metres. */
export const NUDGE_Z_MAX_M = 80;
export const NUDGE_XY_MAX_M = 500;
const NUDGE_MM_HINT_M = 1000;

const emptyNudge = () => ({ x: 0, y: 0, z: 0 });

const axisCenter = (bounds, axis) => {
  if (!bounds) return NaN;
  const c = Number(bounds.center?.[axis]);
  if (Number.isFinite(c)) return c;
  const min = Number(bounds.min?.[axis]);
  const max = Number(bounds.max?.[axis]);
  return Number.isFinite(min) && Number.isFinite(max) ? (min + max) / 2 : NaN;
};

/** ±max(200, 1.5 × AABB/storey separation) so a far-off model is not clamped. */
export function sliderSpanFromAlignContext({
  nativeStoreys,
  datumStoreys,
  datumBounds,
  modelBounds,
} = {}) {
  let sep = 0;
  for (const native of nativeStoreys || []) {
    for (const datum of datumStoreys || []) {
      const a = metersFromMaybeMillimetres(Number(native.elevation), NUDGE_Z_MAX_M);
      const b = metersFromMaybeMillimetres(Number(datum.elevation), NUDGE_Z_MAX_M);
      if (Number.isFinite(a) && Number.isFinite(b)) {
        sep = Math.max(sep, Math.abs(a - b));
      }
    }
  }
  for (const axis of ["x", "y", "z"]) {
    const a = axisCenter(datumBounds, axis);
    const b = axisCenter(modelBounds, axis);
    if (Number.isFinite(a) && Number.isFinite(b)) {
      sep = Math.max(sep, Math.abs(a - b));
    }
  }
  return Math.min(NUDGE_XY_MAX_M, Math.max(SLIDER_BASE_M, Math.ceil(sep * 1.5)));
}

export function metersFromMaybeMillimetres(value, maxM = NUDGE_Z_MAX_M) {
  if (!Number.isFinite(value)) return 0;
  if (Math.abs(value) >= NUDGE_MM_HINT_M && Math.abs(value / 1000) <= maxM * 2) {
    return value / 1000;
  }
  return value;
}

export function clampNudgeMeters(value, maxM) {
  const meters = metersFromMaybeMillimetres(value, maxM);
  if (!Number.isFinite(meters)) return 0;
  return Math.min(maxM, Math.max(-maxM, meters));
}

const clampAxis = (axis, value) =>
  clampNudgeMeters(value, axis === "z" ? NUDGE_Z_MAX_M : NUDGE_XY_MAX_M);

const expandSpan = (span, value) => {
  const abs = Math.abs(value);
  if (abs < span - SLIDER_STEP_M / 2) return span;
  return Math.max(span * 2, Math.ceil(abs + SLIDER_EXPAND_PAD_M));
};

/** Accept comma or dot. Empty / invalid → null (keep typing). */
export function parseNudgeMeters(raw) {
  if (raw == null) return null;
  const text = String(raw).trim().replace(",", ".");
  if (text === "" || text === "-" || text === "." || text === "-.") return null;
  const parsed = Number.parseFloat(text);
  return Number.isFinite(parsed) ? parsed : null;
}

const formatNudgeMeters = (value) => {
  const n = Number(value) || 0;
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

const datumLabel = (storey, t) =>
  storey?.empty
    ? t("ifc_viewer.link_storeys_empty_target", { name: storey.name })
    : storey?.name || "";

/**
 * Left-docked storey-link + visual align dialog.
 * No full-screen scrim — the main iframe stays visible so sliders,
 * typed meter fields and section cuts update the 3D view live.
 */
export function IfcStoreyLinkDialog({
  open,
  proposal,
  savedLinks,
  saving = false,
  onClose,
  onSave,
  onSkip,
  onNudge,
  onSelectStorey,
  datumBounds = null,
  modelBounds = null,
}) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [selectedFromId, setSelectedFromId] = useState("");
  const [nudge, setNudge] = useState(emptyNudge);
  const [nudgeDraft, setNudgeDraft] = useState({ x: "0", y: "0", z: "0" });
  const [sliderSpan, setSliderSpan] = useState(SLIDER_BASE_M);

  const nativeStoreys = proposal?.nativeStoreys || [];
  const datumStoreys = proposal?.datumStoreys || [];
  const proposals = proposal?.proposals || [];
  const modelName = proposal?.modelName || "";
  const datumName = proposal?.datumName || "";

  useEffect(() => {
    if (!open || !proposal) return;
    const savedByFrom = new Map(
      (savedLinks || []).map((link) => [String(link.fromId), link.toId ?? null]),
    );
    const next = (proposal.nativeStoreys || []).map((storey) => {
      const auto = (proposal.proposals || []).find((row) => row.fromId === storey.id);
      const saved = savedByFrom.has(storey.id)
        ? savedByFrom.get(storey.id)
        : undefined;
      const toId = saved !== undefined ? saved : auto?.toId ?? null;
      return {
        fromId: storey.id,
        fromName: storey.name,
        toId,
        proposalToId: auto?.toId ?? null,
        proposalName: auto?.toName || "",
        reason: auto?.reason || "none",
      };
    });
    setRows(next);
    setSelectedFromId(next[0]?.fromId || "");
    setNudge(emptyNudge());
    setNudgeDraft({ x: "0", y: "0", z: "0" });
    setSliderSpan(
      sliderSpanFromAlignContext({
        nativeStoreys: proposal.nativeStoreys,
        datumStoreys: proposal.datumStoreys,
        datumBounds,
        modelBounds,
      }),
    );
    // Init once per open / model — do not reset while the user edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, proposal?.modelId]);

  useEffect(() => {
    if (!open || !proposal) return;
    setSliderSpan((span) =>
      Math.max(
        span,
        sliderSpanFromAlignContext({
          nativeStoreys: proposal.nativeStoreys,
          datumStoreys: proposal.datumStoreys,
          datumBounds,
          modelBounds,
        }),
      ),
    );
  }, [open, proposal, datumBounds, modelBounds]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !saving) onSkip?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, saving, onSkip]);

  const selectedRow = useMemo(
    () => rows.find((row) => row.fromId === selectedFromId) || rows[0] || null,
    [rows, selectedFromId],
  );

  useEffect(() => {
    if (!open || !selectedRow) return;
    const elevation =
      datumStoreys.find((storey) => storey.id === selectedRow.toId)?.elevation ??
      nativeStoreys.find((storey) => storey.id === selectedRow.fromId)?.elevation;
    onSelectStorey?.({
      storeyId: selectedRow.toId || null,
      elevation: Number.isFinite(elevation) ? elevation : null,
    });
  }, [open, selectedRow, datumStoreys, nativeStoreys, onSelectStorey]);

  if (!open || !proposal) return null;

  const reasonLabel = (reason) => {
    if (reason === "name") return t("ifc_viewer.link_storeys_proposal_name");
    if (reason === "elevation") return t("ifc_viewer.link_storeys_proposal_elevation");
    return t("ifc_viewer.link_storeys_proposal_none");
  };

  const setRowToId = (fromId, toId) => {
    setRows((current) =>
      current.map((row) =>
        row.fromId === fromId ? { ...row, toId: toId || null } : row,
      ),
    );
  };

  const emitNudge = (next) => {
    const clamped = {
      x: clampAxis("x", next.x),
      y: clampAxis("y", next.y),
      z: clampAxis("z", next.z),
    };
    setNudge(clamped);
    setNudgeDraft({
      x: formatNudgeMeters(clamped.x),
      y: formatNudgeMeters(clamped.y),
      z: formatNudgeMeters(clamped.z),
    });
    onNudge?.(clamped);
  };

  const handleSlider = (axis, value) => {
    emitNudge({ ...nudge, [axis]: clampAxis(axis, value) });
    if (axis !== "z") setSliderSpan((span) => expandSpan(span, value));
  };

  const handleMeterDraft = (axis, raw) => {
    setNudgeDraft((current) => ({ ...current, [axis]: raw }));
    const parsed = parseNudgeMeters(raw);
    if (parsed == null) return;
    const value = clampAxis(axis, parsed);
    const next = { ...nudge, [axis]: value };
    setNudge(next);
    onNudge?.(next);
    if (axis !== "z") setSliderSpan((span) => expandSpan(span, value));
  };

  const commitMeterDraft = (axis) => {
    const parsed = parseNudgeMeters(nudgeDraft[axis]);
    const value = clampAxis(axis, parsed == null ? nudge[axis] || 0 : parsed);
    emitNudge({ ...nudge, [axis]: value });
    if (axis !== "z") setSliderSpan((span) => expandSpan(span, value));
  };

  const handleResetNudge = () => {
    emitNudge(emptyNudge());
    setSliderSpan(
      sliderSpanFromAlignContext({
        nativeStoreys,
        datumStoreys,
        datumBounds,
        modelBounds,
      }),
    );
  };

  const handleSave = () => {
    onSave?.({
      links: rows.map((row) => ({ fromId: row.fromId, toId: row.toId })),
      nudge,
    });
  };

  return (
    <div
      className="pointer-events-none fixed inset-y-0 left-0 z-[55] flex w-full max-w-[440px] p-3"
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="false"
        aria-label={t("ifc_viewer.link_storeys_title", { name: datumName })}
        className="pointer-events-auto flex max-h-full w-full flex-col overflow-hidden rounded-xl border border-[#e5e8ec] bg-white shadow-xl"
      >
        <div className="flex items-center justify-between gap-2 border-b border-[#e5e8ec] px-4 py-3">
          <h2 className="m-0 text-sm font-semibold text-[#14171c]">
            {t("ifc_viewer.link_storeys_title", { name: datumName })}
          </h2>
          <button
            type="button"
            onClick={() => onSkip?.()}
            disabled={saving}
            aria-label={t("ifc_viewer.link_storeys_skip")}
            className="grid h-8 w-8 place-items-center rounded-lg text-[#59616d] hover:bg-[#eef0f3] disabled:opacity-50"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <p className="m-0 text-[12px] leading-relaxed text-[#59616d]">
            {t("ifc_viewer.link_storeys_hint")}
          </p>

          <div className="overflow-x-auto rounded-[10px] border border-[#e5e8ec]">
            <table className="w-full border-collapse text-left text-[12px]">
              <thead>
                <tr className="bg-[#f6f7f9] text-[10px] font-bold uppercase tracking-[0.04em] text-[#969da8]">
                  <th className="px-2.5 py-2 font-bold">
                    {t("ifc_viewer.link_storeys_col_model", { name: modelName })}
                  </th>
                  <th className="px-2.5 py-2 font-bold">
                    {t("ifc_viewer.link_storeys_col_proposal")}
                  </th>
                  <th className="px-2.5 py-2 font-bold">
                    {t("ifc_viewer.link_storeys_col_datum", { name: datumName })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const active = row.fromId === selectedRow?.fromId;
                  return (
                    <tr
                      key={row.fromId}
                      className={`cursor-pointer border-t border-[#eef1f5] ${
                        active ? "bg-[#eaf1ff]" : "bg-white"
                      }`}
                      onClick={() => setSelectedFromId(row.fromId)}
                    >
                      <td className="px-2.5 py-1.5 font-semibold text-[#14171c]">
                        {row.fromName}
                      </td>
                      <td className="px-2.5 py-1.5 text-[#59616d]">
                        <div>{row.proposalName || "—"}</div>
                        <div className="text-[10px] text-[#969da8]">
                          {reasonLabel(row.reason)}
                        </div>
                      </td>
                      <td className="px-2.5 py-1.5">
                        <select
                          value={row.toId || UNLINK}
                          onChange={(event) => {
                            event.stopPropagation();
                            setSelectedFromId(row.fromId);
                            setRowToId(row.fromId, event.target.value);
                          }}
                          onClick={(event) => event.stopPropagation()}
                          className="w-full rounded-[6px] border border-[#e5e8ec] bg-white px-1.5 py-1 text-[11px] text-[#14171c] focus:border-[#2b6ef0] focus:outline-none"
                          aria-label={t("ifc_viewer.link_storeys_col_datum", {
                            name: datumName,
                          })}
                        >
                          <option value={UNLINK}>
                            {t("ifc_viewer.link_storeys_unlink")}
                          </option>
                          {datumStoreys.map((storey) => (
                            <option key={storey.id} value={storey.id}>
                              {datumLabel(storey, t)}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="space-y-2 rounded-[10px] border border-[#e5e8ec] bg-[#f6f7f9] p-3">
            <div className="text-[11px] font-semibold text-[#334155]">
              {t("ifc_viewer.link_storeys_align")}
            </div>
            <p className="m-0 text-[11px] leading-relaxed text-[#59616d]">
              {t("ifc_viewer.link_storeys_align_hint")}
            </p>
            {[
              ["x", "ifc_viewer.link_storeys_nudge_x"],
              ["y", "ifc_viewer.link_storeys_nudge_y"],
              ["z", "ifc_viewer.link_storeys_nudge_z"],
            ].map(([axis, labelKey]) => (
              <label key={axis} className="flex items-center gap-2">
                <span className="w-[88px] shrink-0 text-[11px] font-semibold text-[#334155]">
                  {t(labelKey)}
                </span>
                <input
                  type="range"
                  min={-(axis === "z" ? NUDGE_Z_MAX_M : sliderSpan)}
                  max={axis === "z" ? NUDGE_Z_MAX_M : sliderSpan}
                  step={SLIDER_STEP_M}
                  value={nudge[axis]}
                  onChange={(event) =>
                    handleSlider(axis, Number.parseFloat(event.target.value) || 0)
                  }
                  aria-label={t(labelKey)}
                  className="w-full accent-[#2b6ef0]"
                />
                <input
                  type="text"
                  inputMode="decimal"
                  value={nudgeDraft[axis]}
                  onChange={(event) => handleMeterDraft(axis, event.target.value)}
                  onBlur={() => commitMeterDraft(axis)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      commitMeterDraft(axis);
                    }
                  }}
                  aria-label={t("ifc_viewer.link_storeys_nudge_meters", {
                    axis: t(labelKey),
                  })}
                  className="w-[72px] shrink-0 rounded-[6px] border border-[#e5e8ec] bg-white px-1.5 py-1 text-right text-[11px] tabular-nums text-[#14171c] focus:border-[#2b6ef0] focus:outline-none"
                />
                <span className="w-4 shrink-0 text-[10px] text-[#59616d]">m</span>
              </label>
            ))}
            <button
              type="button"
              onClick={handleResetNudge}
              className="rounded-[7px] border border-[#e5e8ec] bg-white px-2.5 py-1 text-[11px] font-semibold text-[#14171c] hover:bg-[#eef0f3]"
            >
              {t("ifc_viewer.link_storeys_reset_nudge")}
            </button>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-[#e5e8ec] px-4 py-3">
          <button
            type="button"
            onClick={() => onSkip?.()}
            disabled={saving}
            className="rounded-lg border border-[#e5e8ec] px-3 py-2 text-sm font-semibold text-[#14171c] hover:bg-[#f6f7f9] disabled:opacity-50"
          >
            {t("ifc_viewer.link_storeys_skip")}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-lg bg-[#2b6ef0] px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {t("ifc_viewer.link_storeys_save")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default IfcStoreyLinkDialog;
